import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, CheckCircle, Sparkles, MapPin, FileCheck } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';

export default function GepositPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();

  const services = [
    {
      icon: FileCheck,
      titleKey: 'geposit.services.addressCleaning.title',
      descriptionKey: 'geposit.services.addressCleaning.description',
    },
    {
      icon: CheckCircle,
      titleKey: 'geposit.services.valid.title',
      descriptionKey: 'geposit.services.valid.description',
    },
    {
      icon: MapPin,
      titleKey: 'geposit.services.atlas.title',
      descriptionKey: 'geposit.services.atlas.description',
    },
  ];

  const timeline = [
    { year: '2005', key: 'geposit.timeline.2005' },
    { year: '2010', key: 'geposit.timeline.2010' },
    { year: '2011', key: 'geposit.timeline.2011' },
    { year: t('geposit.timeline.todayLabel'), key: 'geposit.timeline.today' },
  ];

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('geposit.title')}
        description={t('meta.geposit')}
        path="/geposit"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('common.geposit'), url: `https://postnummerservice.se/${lang}/geposit` },
        ])}
      />
      {/* Hero Section */}
      <div className="max-w-3xl mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-secondary" />
          <span className="text-sm font-medium text-secondary uppercase tracking-wide">
            {t('geposit.label')}
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          {t('geposit.title')}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {t('geposit.description')}
        </p>
      </div>

      {/* Timeline Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold mb-8">{t('geposit.historyTitle')}</h2>

        {/* Desktop: Horizontal timeline */}
        <div className="hidden md:block">
          <div className="relative">
            <div className="absolute top-[7px] left-0 right-0 h-0.5 bg-border" />
            <div className="grid grid-cols-4 gap-6 relative">
              {timeline.map((item, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="w-4 h-4 rounded-full bg-primary border-4 border-background relative z-10 mb-4" />
                  <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-2">
                    {item.year}
                  </span>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t(item.key)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: Vertical timeline */}
        <div className="md:hidden">
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="absolute left-4 w-3 h-3 rounded-full bg-primary border-4 border-background -translate-x-1.5 mt-1" />
                  <div className="ml-12">
                    <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-2">
                      {item.year}
                    </span>
                    <p className="text-muted-foreground">{t(item.key)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold mb-2">{t('geposit.servicesTitle')}</h2>
        <p className="text-muted-foreground mb-8">{t('geposit.servicesSubtitle')}</p>
        
        <div className="grid gap-6 md:grid-cols-3">
          {services.map((service, index) => (
            <Card key={index} className="h-full">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-secondary" />
                </div>
                <CardTitle className="text-lg">{t(service.titleKey)}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="leading-relaxed">
                  {t(service.descriptionKey)}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 lg:p-12">
        <h2 className="text-2xl font-semibold mb-4">{t('geposit.cta.title')}</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          {t('geposit.cta.description')}
        </p>
        <Button asChild size="lg">
          <a href="https://geposit.se" target="_blank" rel="noopener noreferrer">
            {t('geposit.visitWebsite')}
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </Button>
      </section>
    </div>
  );
}
